import { useEffect, useState, useCallback } from 'react';
import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import { format } from 'date-fns';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Badge } from '@/client/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import {
  ArrowLeft,
  ArrowLeftRight,
  ExternalLink,
  Copy,
  Check,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Ticket,
  Plus,
  X,
  RefreshCw,
} from 'lucide-react';
import { API_BASE_URL } from '@/client/config';
import { SwapClientDialog, useClientSwapInfo } from '@/client/components/swap-client-dialog';
import {
  PLATFORM_COLORS,
  STATUS_COLORS,
  StatusBadge,
  PlatformBadge,
  formatDate,
} from './index';
import type {
  OTTApp,
  OTTStoreTarget,
  OTTSubmission,
  AppSubmissionStatus,
  AppSubmissionStatusRecord,
} from '@shared/types';

// Optional URL validation - valid URL, empty string, or null
const optionalUrlSchema = z.string().nullable().refine(
  (val) => !val || z.string().url().safeParse(val).success,
  'Please enter a valid URL'
);

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'ott-manager/$apIdx',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: OTTAppDetailComponent,
});

/** API response for single app detail */
interface GetOTTAppDetailResponse {
  app: OTTApp;
  clientID: string;
  clientName: string;
  statuses: AppSubmissionStatusRecord[];
}

/** Platform from API */
interface Platform {
  targetIdx: number;
  targetName: string;
}

/** Track edited submission state */
interface SubmissionEdits {
  storeID?: string | null;
  statusId?: number | null;
  ticketURL?: string | null;
  dateSubmittedVendor?: string | null;
  dateApprovedVendor?: string | null;
  rejectionDate?: string | null;
  rejectionURL?: string | null;
}

/** Copy to clipboard helper */
function useCopyToClipboard() {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  }, []);

  return { copied, copy };
}

/** Parse yyyy-MM-dd string to local Date (avoids timezone offset issues) */
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0); // noon local time to avoid DST edge cases
}

/** Format ISO string to yyyy-MM-dd for input display */
function toDateInputValue(isoString: string | null): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  // Use UTC methods to avoid timezone shift when displaying stored dates
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Date input component for nullable dates */
function DateInput({
  value,
  onChange,
  disabled = false,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}) {
  // Use local state to prevent controlled input from overwriting during typing
  const [localValue, setLocalValue] = useState(toDateInputValue(value));

  // Sync local state when parent value changes (e.g., on save/reset)
  useEffect(() => {
    setLocalValue(toDateInputValue(value));
  }, [value]);

  return (
    <Input
      type="date"
      value={localValue}
      onChange={(e) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (!newValue) {
          onChange(null);
          return;
        }
        // Only propagate to parent if valid date with reasonable year
        const [year] = newValue.split('-').map(Number);
        if (year >= 1900 && year <= 2100) {
          // Create date at noon local time, then convert to ISO
          const localDate = parseLocalDate(newValue);
          onChange(localDate.toISOString());
        }
      }}
      disabled={disabled}
      className={`w-40 cursor-pointer date-input ${!localValue ? 'date-muted' : ''}`}
    />
  );
}

/** Single submission card with editable fields */
function SubmissionCard({
  submission,
  statuses,
  isLatest,
  edits,
  hasStoreIDEdit,
  onEdit,
  onSave,
  saving,
}: {
  submission: OTTSubmission;
  statuses: AppSubmissionStatusRecord[];
  isLatest: boolean;
  edits: SubmissionEdits;
  hasStoreIDEdit: boolean;
  onEdit: (field: keyof SubmissionEdits, value: string | number | null) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const [expanded, setExpanded] = useState(isLatest);

  // Check if there are unsaved changes (including Store ID for latest submission)
  const hasEdits = Object.keys(edits).length > 0 || (isLatest && hasStoreIDEdit);

  // Get current values (edited or original)
  // Use 'in' check for fields that can be explicitly set to null (to clear them)
  const currentStatusId = 'statusId' in edits ? edits.statusId : submission.statusId;
  const currentStatus = statuses.find((s) => s.statusId === currentStatusId)?.statusName ?? submission.statusName;
  const currentTicketURL = ('ticketURL' in edits ? edits.ticketURL : submission.ticketURL) ?? '';
  const currentDateSubmittedVendor = 'dateSubmittedVendor' in edits ? (edits.dateSubmittedVendor ?? null) : submission.dateSubmittedVendor;
  const currentDateApprovedVendor = 'dateApprovedVendor' in edits ? (edits.dateApprovedVendor ?? null) : submission.dateApprovedVendor;
  const currentRejectionDate = 'rejectionDate' in edits ? (edits.rejectionDate ?? null) : submission.rejectionDate;
  const currentRejectionURL = ('rejectionURL' in edits ? edits.rejectionURL : submission.rejectionURL) ?? '';

  const isRejected = currentStatus === 'Rejected' || currentRejectionDate;

  return (
    <div className="border rounded-lg bg-card">
      {/* Header - always visible */}
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-accent/30"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-mono text-muted-foreground">
            #{submission.submissionId}
          </span>
          {isLatest && (
            <Badge variant="outline" className="text-xs">
              Current
            </Badge>
          )}
          <StatusBadge status={currentStatus as AppSubmissionStatus} />
          {submission.ticketURL && (
            <a
              href={submission.ticketURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-foreground"
              title="View ticket"
            >
              <Ticket className="h-4 w-4" />
            </a>
          )}
          {hasEdits && (
            <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800">
              Unsaved
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            {formatDate(submission.dateSubmittedClient)}
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="p-4 border-t space-y-4">
          {/* Status */}
          <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
            <label className="text-sm text-muted-foreground">Status</label>
            <Select
              value={String(currentStatusId ?? '')}
              onValueChange={(val) => onEdit('statusId', parseInt(val, 10))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.statusId} value={String(status.statusId)}>
                    {status.statusName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Ticket URL */}
          <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
            <label className="text-sm text-muted-foreground">Ticket URL</label>
            <Input
              value={currentTicketURL}
              onChange={(e) => onEdit('ticketURL', e.target.value || null)}
              placeholder="https://invintus-support.freshdesk.com/a/tickets/..."
              className="max-w-md"
            />
          </div>

          {/* Dates section */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Dates</h4>

            <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
              <label className="text-sm text-muted-foreground">Client Submitted</label>
              <span className="text-sm">{formatDate(submission.dateSubmittedClient)}</span>
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
              <label className="text-sm text-muted-foreground">Vendor Submitted</label>
              <DateInput
                value={currentDateSubmittedVendor}
                onChange={(val) => onEdit('dateSubmittedVendor', val)}
              />
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
              <label className="text-sm text-muted-foreground">Vendor Approved</label>
              <DateInput
                value={currentDateApprovedVendor}
                onChange={(val) => onEdit('dateApprovedVendor', val)}
              />
            </div>

            <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
              <label className="text-sm text-muted-foreground">Rejection Date</label>
              <div className="flex items-center gap-1">
                <DateInput
                  value={currentRejectionDate}
                  onChange={(val) => onEdit('rejectionDate', val)}
                />
                {currentRejectionDate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit('rejectionDate', null)}
                    title="Clear rejection date"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Rejection URL - only show if rejected */}
          {isRejected && (
            <div className="grid grid-cols-[140px_1fr] gap-2 items-center">
              <label className="text-sm text-muted-foreground">Rejection URL</label>
              <div className="flex items-center gap-2">
                <Input
                  value={currentRejectionURL}
                  onChange={(e) => onEdit('rejectionURL', e.target.value || null)}
                  placeholder="Link to vendor rejection notice"
                  className="max-w-md"
                />
                {currentRejectionURL && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit('rejectionURL', null)}
                    title="Clear rejection URL"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
                {submission.rejectionURL && (
                  <a
                    href={submission.rejectionURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Save button */}
          {hasEdits && (
            <div className="flex justify-end pt-2">
              <Button onClick={onSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Platform card with store ID and submission history */
function PlatformCard({
  target,
  statuses,
  storeIDEdits,
  submissionEdits,
  onStoreIDEdit,
  onSubmissionEdit,
  onSaveSubmission,
  onCreateSubmission,
  savingSubmission,
  creatingSubmission,
}: {
  target: OTTStoreTarget;
  statuses: AppSubmissionStatusRecord[];
  storeIDEdits: Record<number, string>;
  submissionEdits: Record<number, SubmissionEdits>;
  onStoreIDEdit: (idx: number, value: string) => void;
  onSubmissionEdit: (submissionId: number, field: keyof SubmissionEdits, value: string | number | null) => void;
  onSaveSubmission: (submissionId: number) => void;
  onCreateSubmission: (targetIdx: number, storeID?: string) => void;
  savingSubmission: number | null;
  creatingSubmission: number | null;
}) {
  const { copied, copy } = useCopyToClipboard();
  const colors = PLATFORM_COLORS[target.targetName];

  // Get current store ID from latest submission
  const latestSubmission = target.currentSubmission;
  const currentStoreID = storeIDEdits[target.idx] ?? latestSubmission?.storeID ?? '';
  const hasStoreIDEdit = target.idx in storeIDEdits && storeIDEdits[target.idx] !== (latestSubmission?.storeID ?? '');

  // Sort submissions by date (newest first)
  const sortedSubmissions = [...target.submissions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <PlatformBadge platform={target.targetName} />
            {target.currentSubmission && (
              <StatusBadge status={target.currentSubmission.statusName} />
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Store ID */}
        <div className="space-y-2">
          <label className="text-sm font-mediu">Store ID</label>
          <div className="flex items-center gap-2">
            <Input
              value={currentStoreID}
              onChange={(e) => onStoreIDEdit(target.idx, e.target.value)}
              placeholder="Store ID..."
              className="flex-1 font-mono text-sm"
            />
            {currentStoreID && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => copy(currentStoreID, `store-${target.idx}`)}
              >
                {copied === `store-${target.idx}` ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          {hasStoreIDEdit && (
            <p className="text-xs text-amber-600">Store ID will be saved with the submission</p>
          )}
        </div>

        {/* Submission history */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              Submission History ({target.submissions.length})
            </h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCreateSubmission(target.idx, currentStoreID || undefined)}
              disabled={creatingSubmission === target.idx}
            >
              {creatingSubmission === target.idx ? (
                'Creating...'
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-1" />
                  New Submission
                </>
              )}
            </Button>
          </div>
          {sortedSubmissions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No submissions yet</p>
          ) : (
            <div className="space-y-2">
              {sortedSubmissions.map((submission, idx) => (
                <SubmissionCard
                  key={submission.submissionId}
                  submission={submission}
                  statuses={statuses}
                  isLatest={idx === 0}
                  edits={submissionEdits[submission.submissionId] ?? {}}
                  hasStoreIDEdit={hasStoreIDEdit}
                  onEdit={(field, value) => onSubmissionEdit(submission.submissionId, field, value)}
                  onSave={() => onSaveSubmission(submission.submissionId)}
                  saving={savingSubmission === submission.submissionId}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function OTTAppDetailComponent() {
  const navigate = useNavigate();
  const { apIdx } = Route.useParams();
  const { getAccessTokenSilently } = useAuth0();

  const [app, setApp] = useState<OTTApp | null>(null);
  const [clientID, setClientID] = useState('');
  const [clientName, setClientName] = useState('');
  const [statuses, setStatuses] = useState<AppSubmissionStatusRecord[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [storeIDEdits, setStoreIDEdits] = useState<Record<number, string>>({});
  const [submissionEdits, setSubmissionEdits] = useState<Record<number, SubmissionEdits>>({});
  const [savingSubmission, setSavingSubmission] = useState<number | null>(null);
  const [creatingSubmission, setCreatingSubmission] = useState<number | null>(null);

  // Add platform state
  const [addingPlatform, setAddingPlatform] = useState<number | null>(null);

  // Swap client state
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);

  const { copied, copy } = useCopyToClipboard();

  // Check if client swap is needed
  const { needsSwap } = useClientSwapInfo(clientID);

  // openAfterSwap is handled globally in __root.tsx

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();

      // Fetch app detail and platforms in parallel
      const [appResponse, platformsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ott-manager/${apIdx}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/ott-manager/platforms`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (!appResponse.ok) {
        if (appResponse.status === 404) {
          throw new Error(`App with ID ${apIdx} not found`);
        }
        throw new Error(`Failed to fetch: ${appResponse.statusText}`);
      }

      const [appData, platformsData] = await Promise.all([
        appResponse.json(),
        platformsResponse.ok ? platformsResponse.json() : { platforms: [] },
      ]);

      setApp(appData.app);
      setClientID(appData.clientID);
      setClientName(appData.clientName);
      setStatuses(appData.statuses);
      setPlatforms(platformsData.platforms ?? []);
    } catch (err) {
      console.error('Error fetching app detail:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [apIdx, getAccessTokenSilently]);

  const handleStoreIDEdit = (idx: number, value: string) => {
    setStoreIDEdits((prev) => ({ ...prev, [idx]: value }));
  };

  const handleSubmissionEdit = (
    submissionId: number,
    field: keyof SubmissionEdits,
    value: string | number | null
  ) => {
    setSubmissionEdits((prev) => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        [field]: value,
      },
    }));
  };

  const handleSaveSubmission = async (submissionId: number) => {
    const edits = submissionEdits[submissionId] ?? {};

    // Validate URL fields before saving
    const urlValidations: { field: string; value: string | null | undefined }[] = [
      { field: 'Ticket URL', value: edits.ticketURL },
      { field: 'Rejection URL', value: edits.rejectionURL },
    ];

    for (const { field, value } of urlValidations) {
      if (value !== undefined) {
        const result = optionalUrlSchema.safeParse(value);
        if (!result.success) {
          setError(`${field}: ${result.error.issues[0].message}`);
          return;
        }
      }
    }

    setSavingSubmission(submissionId);

    try {
      const token = await getAccessTokenSilently();

      // Find which target this submission belongs to (for Store ID edit lookup)
      let targetIdx: number | null = null;
      for (const target of app?.storeTargets ?? []) {
        if (target.submissions.some((s) => s.submissionId === submissionId)) {
          targetIdx = target.idx;
          break;
        }
      }

      // Include Store ID edit if present for this target
      // Empty string means user wants to clear it
      const payload: SubmissionEdits & { storeID?: string | null } = { ...edits };
      if (targetIdx !== null && targetIdx in storeIDEdits) {
        payload.storeID = storeIDEdits[targetIdx];
      }

      const response = await fetch(`${API_BASE_URL}/api/ott-manager/submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to save: ${response.statusText}`);
      }

      // Clear edits for this submission
      setSubmissionEdits((prev) => {
        const next = { ...prev };
        delete next[submissionId];
        return next;
      });

      // Clear Store ID edit for this target if it was included
      if (targetIdx !== null && targetIdx in storeIDEdits) {
        setStoreIDEdits((prev) => {
          const next = { ...prev };
          delete next[targetIdx];
          return next;
        });
      }

      // Refresh data to get updated values
      await fetchData();
    } catch (err) {
      console.error('Error saving submission:', err);
      setError(err instanceof Error ? err.message : 'Failed to save submission');
    } finally {
      setSavingSubmission(null);
    }
  };

  const handleAddPlatform = async (targetIdx: number) => {
    setAddingPlatform(targetIdx);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE_URL}/api/ott-manager/targets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          apIdx: parseInt(apIdx, 10),
          targetIdx,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to add platform: ${response.statusText}`);
      }

      // Refresh data to get new store target
      await fetchData();
    } catch (err) {
      console.error('Error adding platform:', err);
      setError(err instanceof Error ? err.message : 'Failed to add platform');
    } finally {
      setAddingPlatform(null);
    }
  };

  const handleCreateSubmission = async (targetIdx: number, storeID?: string) => {
    setCreatingSubmission(targetIdx);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${API_BASE_URL}/api/ott-manager/submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          appsToTargetsIdx: targetIdx,
          storeID: storeID || null,
          statusId: 1, // Default to Draft
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create: ${response.statusText}`);
      }

      // Clear store ID edit for this target since it's now saved
      setStoreIDEdits((prev) => {
        const next = { ...prev };
        delete next[targetIdx];
        return next;
      });

      // Refresh data to get new submission
      await fetchData();
    } catch (err) {
      console.error('Error creating submission:', err);
      setError(err instanceof Error ? err.message : 'Failed to create submission');
    } finally {
      setCreatingSubmission(null);
    }
  };

  return (
    <Layout>
      <div>
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/ott-manager' })}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to OTT Manager
        </Button>

        {loading && <p>Loading...</p>}

        {error && (
          <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded">
            <p className="m-0 text-red-800 font-medium">Error: {error}</p>
          </div>
        )}

        {!loading && !error && app && (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl font-semibold">{app.appName}</h1>
              </div>
              {(() => {
                const hasUnsavedEdits =
                  Object.keys(submissionEdits).length > 0 ||
                  Object.keys(storeIDEdits).length > 0;

                const editorDisabled = needsSwap || hasUnsavedEdits;
                const editorTitle = needsSwap
                  ? 'Switch to this client first'
                  : 'Save changes before opening editor';

                return (
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Client: {clientName}</span>
                    <span>|</span>
                    <span>Manifest Updated: {formatDate(app.manifestUpdatedAt)}</span>
                    <span>|</span>
                    {needsSwap && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSwapDialogOpen(true)}
                          className="h-7"
                        >
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Switch to {clientName}
                        </Button>
                        <ArrowLeftRight className="h-3 w-3 text-muted-foreground/50" />
                      </>
                    )}
                    {editorDisabled ? (
                      <span
                        className="flex items-center gap-1 text-muted-foreground/50 cursor-not-allowed"
                        title={editorTitle}
                      >
                        Open OTT Editor
                        <ExternalLink className="h-3 w-3" />
                      </span>
                    ) : (
                      <a
                        href={app.editorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-primary hover:underline"
                      >
                        Open OTT Editor
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Swap Client Dialog */}
            <SwapClientDialog
              open={swapDialogOpen}
              onOpenChange={setSwapDialogOpen}
              targetClientID={clientID}
              targetClientName={clientName}
              hasUnsavedChanges={
                Object.keys(submissionEdits).length > 0 ||
                Object.keys(storeIDEdits).length > 0
              }
            />

            {/* Platform cards */}
            <div className="space-y-4">
              {app.storeTargets.map((target) => (
                <PlatformCard
                  key={target.idx}
                  target={target}
                  statuses={statuses}
                  storeIDEdits={storeIDEdits}
                  submissionEdits={submissionEdits}
                  onStoreIDEdit={handleStoreIDEdit}
                  onSubmissionEdit={handleSubmissionEdit}
                  onSaveSubmission={handleSaveSubmission}
                  onCreateSubmission={handleCreateSubmission}
                  savingSubmission={savingSubmission}
                  creatingSubmission={creatingSubmission}
                />
              ))}

              {/* Add Platform section */}
              {(() => {
                // Filter out platforms that are already added
                const existingTargetIdxs = new Set(app.storeTargets.map((t) => t.targetIdx));
                const availablePlatforms = platforms.filter((p) => !existingTargetIdxs.has(p.targetIdx));

                if (availablePlatforms.length === 0 && app.storeTargets.length > 0) {
                  return null; // All platforms already added
                }

                return (
                  <Card className={app.storeTargets.length === 0 ? '' : 'border-dashed'}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        {app.storeTargets.length === 0
                          ? 'Add Store Targets'
                          : 'Add Another Platform'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {app.storeTargets.length === 0 && (
                        <p className="text-sm text-muted-foreground mb-4">
                          No store targets configured for this app. Select platforms to add:
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {availablePlatforms.map((platform) => (
                          <Button
                            key={platform.targetIdx}
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddPlatform(platform.targetIdx)}
                            disabled={addingPlatform !== null}
                          >
                            {addingPlatform === platform.targetIdx ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                                Adding...
                              </>
                            ) : (
                              <>
                                <Plus className="h-4 w-4 mr-1" />
                                {platform.targetName}
                              </>
                            )}
                          </Button>
                        ))}
                        {availablePlatforms.length === 0 && (
                          <p className="text-sm text-muted-foreground">
                            All available platforms have been added.
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
